import { BackendRemote } from './backend-com'
import { getDeviceChatId, marknoticedChat } from './backend/chat'
import { instanceHidesPublicSuggestions } from './utils/slothfulInstanceConfig'

export async function updateDeviceChat(
  accountId: number,
  skipCurrentChangelog: boolean = false
) {
  const addDeviceMessage = async (
    label: string,
    msg: Partial<Parameters<typeof BackendRemote.rpc.addDeviceMessage>[2]>
  ) => {
    if (skipCurrentChangelog) {
      await BackendRemote.rpc.addDeviceMessage(accountId, label, null)
    } else {
      await BackendRemote.rpc.addDeviceMessage(accountId, label, {
        text: null,
        html: null,
        viewtype: null,
        file: null,
        filename: null,
        location: null,
        overrideSenderName: null,
        quotedMessageId: null,
        quotedText: null,
        ...msg,
      })
    }
  }
  // Upstream's `changelog-version-2.3.0` entry is deliberately gone: it
  // announces what Delta Chat 2.0 brought over 1.x, and no SlothfulChat
  // release ever predates that, so every account here would be told about
  // an upgrade it never lived through.
  for (const [version, text] of SLOTHFUL_RELEASE_MESSAGES) {
    await addDeviceMessage(`changelog-version-${version}`, { text })
  }
}

/**
 * One "what's new" message per SlothfulChat release, oldest first. For most
 * users this is the only release note they ever read, so it is the release's
 * user-facing summary rather than a changelog dump — see RELEASING.md, which
 * also requires a human to approve the text before it ships.
 *
 * Appending here (rather than one patch per release) keeps the whole history
 * in a single patch that later releases amend. The `changelog-version-` label
 * makes core drop every repeat, so re-runs are free and existing accounts get
 * only the versions they have not seen.
 *
 * Plain text, not markdown: device messages render literally and URLs
 * autolink. The trailing link uses the version's own changelog anchor, so it
 * keeps pointing at this release once later ones ship.
 */
const SLOTHFUL_RELEASE_MESSAGES: ReadonlyArray<readonly [string, string]> = [
  [
    '0.9.0',
    `SlothfulChat 0.9.0

• Voice messages have a real player: seek on the waveform, change speed, and keep listening while you switch chats.
• Big attachments show their type, size and a preview before you download them, with a live percentage — and an interrupted download resumes instead of restarting.
• New long messages can be expanded in the chat to read them in full, and you can copy the text of several messages at once.
• Faster: adding a profile is near-instant, and encryption no longer stalls the app.
• Privacy and security: exported backups no longer linger in browser storage, no usage statistics leave before you have seen the opt-out notice, and answering a call no longer starts your camera by itself.

https://web.slothful.chat/changelog/?p=web-app#v-0.9.0`,
  ],
]

/**
 * SlothfulChat's own welcome message: what this fork does differently (runs in
 * the browser, back up your data, prototyping ground, self-host/fork it).
 *
 * Not part of updateDeviceChat(): that one is skipped for freshly created
 * accounts, and it runs before configure(), which is when core adds its own
 * welcome message — ours has to land after it. Called from changeScreen()
 * instead, on every route into the main screen (all of which mean the account
 * is configured); the label makes core drop every repeat, so the re-runs on
 * each account switch are free.
 */
export async function addSlothfulWelcomeMessage(accountId: number) {
  const tx = window.static_translate
  // The contact-the-developer paragraph points at the New Chat entry from the
  // community suggestions, which an operator can switch off instance-wide —
  // don't send users looking for something that isn't in their build.
  const text = [
    tx('slothful_welcome_message'),
    ...(instanceHidesPublicSuggestions()
      ? []
      : [tx('slothful_welcome_contact_developer')]),
    tx('slothful_welcome_selfhost'),
  ].join('\n\n')
  await BackendRemote.rpc.addDeviceMessage(accountId, 'slothful-welcome', {
    text,
    html: null,
    viewtype: null,
    file: null,
    filename: null,
    location: null,
    overrideSenderName: null,
    quotedMessageId: null,
    quotedText: null,
  })
}

export async function updateDeviceChats() {
  const selectedAccount = await BackendRemote.rpc.getSelectedAccountId()
  for (const accountId of await BackendRemote.rpc.getAllAccountIds()) {
    await updateDeviceChat(accountId, false)
    if (accountId !== selectedAccount) {
      const devChatId = await getDeviceChatId(accountId)
      if (devChatId) marknoticedChat(accountId, devChatId)
    }
  }
}
